import { Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { LibraryModule } from "../../app/shared/lib/lib.module";
import { Router } from "@angular/router";
import { DateSelectionService } from "../services/date-selection.service";
import { MealService } from "../core/services/meal.service";
import { UserService } from "../core/services/user.service";
import { generateDateOptions, getUserCount as getUserCountCommon, getMealData as getMealDataCommon, DateOptionWithBadge, applyMealMenusToDateOptions } from "../shared/utils/commenFunctions";
import { pageLoadAnimationBottomToCenter } from "../shared/utils/common-animations";
import { getUserDetails } from "../shared/utils/storage-utils";
import { scrollToTop } from "../shared/utils/scroll-utils";
import { MealMenuAdminService } from "../core/services/meal-menu-admin.service";

type DateCardState = "today" | "selected" | "default";

interface DateCardOption extends DateOptionWithBadge {
  state: DateCardState;
  submitted: boolean;
  hotMenuText?: string;
  coldMenuText?: string;
}

@Component({
  selector: "app-date-selection",
  templateUrl: "./date-selection.component.html",
  styleUrls: ["./date-selection.component.css"],
  imports: [CommonModule, LibraryModule],
  animations: [pageLoadAnimationBottomToCenter]
})
export class DateSelectionComponent implements OnInit {
  constructor(
    private readonly router: Router,
    private readonly dateSelectionService: DateSelectionService,
    private readonly mealService: MealService,
    private readonly userService: UserService,
    private readonly mealMenuAdminService: MealMenuAdminService,
  ) {}
  userDetails:any = getUserDetails();
  dateOptions: DateCardOption[] = [];
  pageLoadState = 'hidden';
  allDaysSelected = false;
  mealData: any = null
  userCount: any = null;
  private todayDateValue = '';
  private mealMenus: Array<{
    day?: string | null;
    hotMeal?: string | null;
    coldMeal?: string | null;
    vegHotMeal?: string | null;
    vegColdMeal?: string | null;
    nonVegHotMeal?: string | null;
    nonVegColdMeal?: string | null;
    specialEvents?: string | null;
  }> = [];
  private mealDietPreference: string | null = null;
  private readonly headerCopy = {
    default: {
      title: "Meal and Transportation Scheduled Confirmed.",
      subtitle: "Do you want to Prebook for the Coming Days?"
    },
    allSelected: {
      title: "Your Meals & Transportation Are Scheduled!",
      subtitle: "To Make Changes, Just Tap On A Date Below."
    }
  } as const;

  handleCardClick(index: number): void {
    // When clicking a date card, navigate to meal preference for that date
    // Set the current editing date so meal preference knows which date is being selected
    this.dateSelectionService.setCurrentEditingDate(index);
    sessionStorage.setItem('dateValue', this.dateOptions[index].dateValue ?? ''  );
    
    // Store current route so meal-preferance component knows where to navigate back
    sessionStorage.setItem('previousRoute', this.router.url);
    
    // Navigate to meal preference to select meal and transport for this date
    this.router.navigate(['/meal-preferance'], { queryParams: { date: this.dateOptions[index].dateValue } });
  }

  handleCardKeyDown(event: KeyboardEvent, index: number): void {
    // Handle Enter or Space key to trigger card click (accessibility)
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.handleCardClick(index);
    }
  }

  ngOnInit(): void {
    scrollToTop();
    
    // Generate date options dynamically (today + 3 more days)
    this.dateOptions = generateDateOptions().map((option, index) => ({
      ...option,
      state: index === 0 ? 'today' : 'default',
      badgeCount: 0,
      submitted: false,
      hotMenuText: '',
      coldMenuText: '',
    }));
    this.todayDateValue = this.dateOptions[0]?.dateValue ?? '';
    this.loadMealMenus();
    
    this.getMealData();
    this.getUserCount();
  }

  private loadMealMenus(): void {
    const userId = Number(this.userDetails?.value);
    this.mealMenuAdminService
      .listMenus(Number.isFinite(userId) && userId > 0 ? userId : null)
      .subscribe({
        next: (response) => {
          this.mealMenus = response?.menus ?? [];
          this.mealDietPreference = response?.mealDietPreference ?? null;
          this.applyMenusToCards();
        },
        error: () => {
          this.mealMenus = [];
        },
      });
  }

  private applyMenusToCards(): void {
    if (!this.mealMenus.length) {
      return;
    }
    this.dateOptions = applyMealMenusToDateOptions(
      this.dateOptions,
      this.mealMenus,
      this.mealDietPreference,
    );
  }

  private syncSelectionStateWithApi(): void {
    const futureOptions = this.dateOptions.filter(option => option.dateValue !== this.todayDateValue);
    this.allDaysSelected = futureOptions.length > 0 && futureOptions.every(option => option.state === 'selected');

    this.dateOptions = this.dateOptions.map((option, idx) => {
      if (option.dateValue === this.todayDateValue && option.state !== 'selected') {
        return { ...option, state: 'today' };
      }
      return option;
    });
  }

  get headerTitle(): string {
    return this.allDaysSelected ? this.headerCopy.allSelected.title : this.headerCopy.default.title;
  }

  get headerSubtitle(): string {
    return this.allDaysSelected ? this.headerCopy.allSelected.subtitle : this.headerCopy.default.subtitle;
  }

  ngAfterViewInit(): void {
    // Prevent scroll on page load
    scrollToTop();
    
    // Trigger animation immediately after view init
    // Use a small delay to ensure Angular has processed the initial state
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.pageLoadState = 'visible';
        // Ensure scroll position remains at top after animation
        scrollToTop();
      }, 50);
    });
  }
  onBackClick(): void {
    this.router.navigate(['/select-meal-transport']);
  }
  onNextClick(): void {
    // Only navigate if at least one date is selected
    const hasSelectedDates = this.dateOptions.some(option =>
      option.dateValue !== this.todayDateValue && option.state === 'selected'
    );
    if (hasSelectedDates) {
      // Clear any previous editing date context
      this.dateSelectionService.setCurrentEditingDate(null);
      
      // Store current route so meal-preferance component knows where to navigate back
      sessionStorage.setItem('previousRoute', this.router.url);
      
      this.router.navigate(['/activity-screen']);
    }
  }

  getMealData(): void {
    getMealDataCommon(
      this.mealService,
      this.userDetails.value,
      this.dateOptions,
      (response: any) => {
        this.mealData = response;
        // Update date options based on meal data
        this.updateDateOptionsFromMealData();
      },
      (error: any) => {
        this.mealData = null;
      }
    );
  }

  private updateDateOptionsFromMealData(): void {
    if (!this.mealData) {
      return;
    }
    
    // Extract dates from mealData response
    // API response structure: { statusCode: 200, message: "...", results: [{ mealDate: "2025-11-24", ... }] }
    const mealDates = new Set<string>();
    const deliveredDates = new Set<string>();
    const mealCountMap = new Map<string, number>();
    const locationNameMap = new Map<string, string>();

    // Helper function to process a meal item
    const processMealItem = (item: any, dateKey: string) => {
      const date = item[dateKey];
      if (!date) {
        return;
      }
      const prefs = item.mealPreference;
      const hasPreference =
        Array.isArray(prefs) &&
        prefs.some(
          (pref: any) =>
            !!pref?.preference || !!pref?.drinks || !!pref?.transport
        );
      // Skip cleared/deselected rows (empty meal preference).
      if (!hasPreference) {
        if (item.submitted) {
          this.dateOptions.forEach(option => {
            if (option.dateValue === date) {
              option.submitted = true;
            }
          });
        }
        return;
      }
      mealDates.add(date);
      mealCountMap.set(date, item.count !== undefined ? item.count : 0);
      if (item.locationName) {
        locationNameMap.set(date, item.locationName);
      }
      if (item.deliveryStatus && item.deliveryStatus.toLowerCase() === 'delivered') {
        deliveredDates.add(date);
      }
      if (item.submitted) {
        this.dateOptions.forEach(option => {
          if (option.dateValue === date) {
            option.submitted = true;
          }
        });
      }
    };
    
    // Check if mealData has results array
    if (this.mealData.results && Array.isArray(this.mealData.results)) {
      this.mealData.results.forEach((item: any) => {
        processMealItem(item, 'mealDate');
      });
    } else if (Array.isArray(this.mealData)) {
      // Fallback: if mealData is directly an array
      this.mealData.forEach((item: any) => {
        processMealItem(item, 'mealDate');
        if (!item.mealDate && item.date) {
          processMealItem(item, 'date');
        }
      });
    }
    
    // Helper function to update option state, badge count and location name
    const updateOptionState = (option: DateCardOption, hasMealInfo: boolean, isToday: boolean): DateCardOption => {
      let newState: DateCardState;
      if (isToday) {
        newState = hasMealInfo ? 'selected' : 'today';
      } else {
        newState = hasMealInfo ? 'selected' : 'default';
      }

      let badgeCount: number;
      if (hasMealInfo && option.dateValue && mealCountMap.has(option.dateValue)) {
        badgeCount = mealCountMap.get(option.dateValue) ?? 0;
      } else {
        badgeCount = option.badgeCount ?? 0;
      }

      const locationName = option.dateValue ? (locationNameMap.get(option.dateValue) ?? '') : '';

      return {
        ...option,
        state: newState,
        badgeCount: badgeCount,
        locationName: locationName
      };
    };
    
    // Update date options: mark as selected if date exists in mealData
    this.dateOptions = this.dateOptions
      .filter(option => !(option.dateValue && deliveredDates.has(option.dateValue)))
      .map((option) => {
        const hasMealInfo = !!(option.dateValue && mealDates.has(option.dateValue));
        const isToday = option.dateValue === this.todayDateValue;
        return updateOptionState(option, hasMealInfo, isToday);
      });
    
    // Ensure UI states reflect API-driven selections
    this.syncSelectionStateWithApi();
    this.applyMenusToCards();

  }
  getUserCount(): void {
    getUserCountCommon(
      this.userService,
      this.dateOptions,
      (updatedOptions) => {
        // Only update badgeCount for dates that are NOT selected
        // For selected dates, preserve the count from mealData
        const updatedMap = new Map<string, DateOptionWithBadge>();
        updatedOptions.forEach(option => {
          if (option.dateValue) {
            updatedMap.set(option.dateValue, option);
          }
        });

        this.dateOptions = this.dateOptions.map((currentOption) => {
          const updatedOption = currentOption.dateValue ? updatedMap.get(currentOption.dateValue) : null;
          if (updatedOption) {
            // Only update badgeCount if the date is NOT selected
            // For selected dates, preserve the count from mealData
            if (currentOption.state !== 'selected' && updatedOption.badgeCount !== undefined) {
              return {
                ...currentOption,
                badgeCount: updatedOption.badgeCount
              };
            }
          }
          return currentOption;
        });
      }
    );
  }
}